import { useRef, useState } from 'react';
/** Pointer coordinates are relative to the video stage, never the chat rail. */
export default function useSelfViewPosition() {
    const stage = useRef(null);
    const tile = useRef(null);
    const [corner, setCorner] = useState({ right: true, bottom: true });
    const [position, setPosition] = useState(null);
    const drag = useRef(null);
    const move = (event) => {
        const parent = stage.current?.getBoundingClientRect();
        const child = tile.current?.getBoundingClientRect();
        if (!parent || !child || drag.current?.id !== event.pointerId)
            return null;
        const point = {
            x: Math.max(8, Math.min(parent.width - child.width - 8, event.clientX - parent.left - drag.current.offset.x)),
            y: Math.max(56, Math.min(parent.height - child.height - 88, event.clientY - parent.top - drag.current.offset.y))
        };
        setPosition(point);
        return { right: point.x + child.width / 2 > parent.width / 2,
            bottom: point.y + child.height / 2 > parent.height / 2 };
    };
    const finish = (event) => {
        if (drag.current?.id !== event.pointerId)
            return;
        const next = move(event);
        if (next)
            setCorner(next);
        drag.current = null;
        setPosition(null);
    };
    const onKeyDown = (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key))
            return;
        event.preventDefault();
        event.stopPropagation();
        setCorner(current => ({
            right: event.key === 'ArrowLeft' ? false : event.key === 'ArrowRight' ? true : current.right,
            bottom: event.key === 'ArrowUp' ? false : event.key === 'ArrowDown' ? true : current.bottom
        }));
    };
    // Anchors remain inside the stage on resize, rotation and chat docking.
    const style = position
        ? { left: position.x, top: position.y }
        : { [corner.right ? 'right' : 'left']: 8, [corner.bottom ? 'bottom' : 'top']: corner.bottom ? 88 : 56 };
    return { stage, tile, style, handle: {
            'aria-label': `Move self view, ${corner.bottom ? 'bottom' : 'top'} ${corner.right ? 'right' : 'left'}. Drag or use arrow keys.`,
            onKeyDown,
            onPointerDown: (event) => {
                const child = tile.current?.getBoundingClientRect();
                if (!child || !event.isPrimary || event.button !== 0)
                    return;
                event.preventDefault();
                drag.current = { id: event.pointerId, offset: { x: event.clientX - child.left, y: event.clientY - child.top } };
                event.currentTarget.setPointerCapture(event.pointerId);
            },
            onPointerMove: move,
            onPointerUp: finish,
            onPointerCancel: () => { drag.current = null; setPosition(null); },
            onLostPointerCapture: () => { drag.current = null; setPosition(null); }
        } };
}
